import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ASSET_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Public asset URLs (from customer-assets)
export const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/wu834haa_SPY%20WHITE.png",
  logoCover: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/dw6nb8eh_DJ%20SPY%20LOGO%20small.JPG",
  // Local fallback served from `frontend/public/logo.png`. Used everywhere
  // a track cover is genuinely missing so the placeholder is guaranteed to
  // return HTTP 200 on the production domain (and matches what SSR /
  // MusicRecording JSON-LD point at).
  trackFallbackCover: "/logo.png",
  heroVideo: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/j224tr0b_DJ%20Showcase%20Spy%20Updated.mov",
  portrait1: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/4s8ayza7_IMG_0930.webp",
  portrait2: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/hu4re5t2_IMG_0931.webp",
  portrait3: "https://customer-assets.emergentagent.com/job_smooth-animation-hub-3/artifacts/zd48gv67_IMG_0932.webp",
};

/**
 * Resolve a track's cover URL for use in <img src=...>. Falls back to
 * `/logo.png` when the record has no cover_url so we never emit an empty
 * src that breaks the render. Handles absolute URLs, relative API paths,
 * and the empty-string case in one place.
 */
export const resolveCoverUrl = (coverUrl) => {
  if (!coverUrl) return ASSETS.trackFallbackCover;
  if (String(coverUrl).startsWith("http")) return coverUrl;
  return `${ASSET_BASE}${coverUrl}`;
};

export const api = axios.create({ baseURL: API, timeout: 20000 });

/**
 * First-party pseudonymous device ID for anti-abuse observation (B4-E).
 *
 * Privacy contract (approved):
 *   • Random UUIDv4 per browser
 *   • Stored ONLY in a first-party cookie named `djs_did`
 *   • Attributes: Path=/, SameSite=Lax, Secure (on HTTPS), Max-Age ≈ 365 days
 *   • NO localStorage, NO sessionStorage, NO canvas/font/WebGL/audio
 *     fingerprinting, NO IndexedDB, NO other persistence layer.
 *   • If cookies are unavailable/blocked the header is simply omitted —
 *     the server treats missing device identity as neutral (no penalty).
 *
 * The raw UUID never leaves the browser as-is; the server hashes it
 * server-side via HMAC-SHA256 with a private pepper before any storage.
 */
const DEVICE_ID_KEY = "djs_did";
const DEVICE_ID_MAX_AGE_S = 365 * 24 * 60 * 60;
const _UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function _uuidV4() {
  // crypto.randomUUID exists in all evergreen browsers; fallback to
  // getRandomValues for the small tail of legacy engines.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const b = new Uint8Array(16);
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    crypto.getRandomValues(b);
  } else {
    for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  }
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (v) => v.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20)}`;
}

function _readCookie(name) {
  try {
    if (typeof document === "undefined") return null;
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : null;
  } catch { return null; }
}

function _writeCookie(name, value) {
  // Returns true on best-effort write, false if the environment blocks
  // cookies entirely.  We NEVER fall back to another store.
  try {
    if (typeof document === "undefined") return false;
    const secure = (typeof window !== "undefined" && window.location.protocol === "https:")
      ? "; Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${DEVICE_ID_MAX_AGE_S}; Path=/; SameSite=Lax${secure}`;
    // Confirm write took effect (browsers with third-party-cookie / all-
    // cookies blocked will silently drop it).
    return _readCookie(name) === value;
  } catch { return false; }
}

/**
 * Return the browser's device ID or `null`.
 *
 * Fix 17A (correction): READ-ONLY. Never writes to `document.cookie`
 * because the canonical `djs_did` cookie is now managed server-side
 * as HttpOnly + Secure + SameSite=Lax by the FastAPI middleware
 * `djs_did_cookie` in server.py. A JS write here would overwrite the
 * HttpOnly cookie in place and downgrade its security posture.
 *
 * Legacy behaviour: for pre-migration browsers that still hold a
 * non-HttpOnly JS-visible `djs_did`, this function returns that value
 * so the existing anti-abuse `X-Device-Id` header interceptor keeps
 * working until the middleware re-issues the HttpOnly cookie on the
 * next `/api/*` response. Post-migration this returns `null` for that
 * browser — the neutral "cookies-blocked" contract already handles
 * that case throughout the app.
 */
export function getDeviceId() {
  const existing = _readCookie(DEVICE_ID_KEY);
  if (existing && _UUID_RE.test(existing)) return existing;
  return null;
}

// Attach X-Device-Id ONLY to endpoints that the anti-abuse layer
// actually observes.  We deliberately do NOT ship the header on every
// request to keep the footprint minimal.
const _DEVICE_ID_PATHS = [
  "/auth/signup",
  "/checkout/",           // includes /checkout/session, /checkout/free-track, ...
  "/entitlement/summary",
];

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("djspy_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  try {
    const url = String(config.url || "");
    if (_DEVICE_ID_PATHS.some((p) => url.startsWith(p))) {
      const did = getDeviceId();
      if (did) config.headers["X-Device-Id"] = did;
      // else: cookie unavailable → omit the header; server treats as neutral.
    }
  } catch { /* noop */ }
  return config;
});

// Response interceptor — surface network / server errors without breaking callers
api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Best-effort log; keep the original rejection so callers still see it
    try {
      // Lazy-load to avoid circular import at module init
      // eslint-disable-next-line
      const { logClientError } = require("@/lib/logger");
      logClientError("api.error", {
        url: err?.config?.url,
        method: err?.config?.method,
        status: err?.response?.status,
        msg: err?.message,
      });
    } catch { /* noop */ }
    return Promise.reject(err);
  }
);

export const assetUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${ASSET_BASE}${u}`;
};

export function getUser() {
  try {
    const raw = localStorage.getItem("djspy_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(token, user) {
  localStorage.setItem("djspy_token", token);
  localStorage.setItem("djspy_user", JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem("djspy_token");
  localStorage.removeItem("djspy_user");
}
